import type { ComponentType } from 'react'
import { useCallback, useState, useMemo } from 'react'
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import {
  authService,
  firebaseLogout,
  useAuthStore,
  useVerticalModule,
} from '@lipaflow/shared'
import {
  HomeIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChartBarSquareIcon,
  EllipsisHorizontalCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { DarkModeToggle, ImpersonationBanner, Logo } from '@lipaflow/ui'
import NotificationBell from '../ui/NotificationBell'
import CommandPalette from '../ui/CommandPalette'
import EmailBouncedBanner from '../ui/EmailBouncedBanner'
import PhoneLinkBanner from '../ui/PhoneLinkBanner'
import DemoModeBanner from '../ui/DemoModeBanner'
import BillingBanner from '../billing/BillingBanner'
import DemoActionModal from '../ui/DemoActionModal'
import SetupInProgressBanner from '../ui/SetupInProgressBanner'

import { hubForPath, useNavigation, type NavItem } from '../../lib/useNavigation'
import { PageTitleProvider } from '../../lib/PageTitleProvider'
import { useDrawerFocus } from '../../lib/useDrawerFocus'
import { usePageTitleContext } from '../../lib/pageTitle'
import { useSharedPage } from '../../lib/sharedPage'

/** Highest priority first: acting as someone else, then money, setup, email, phone, demo. */
const BANNERS: [string, ComponentType][] = [
  ['impersonation', ImpersonationBanner], ['billing', BillingBanner], ['setup', SetupInProgressBanner],
  ['email-bounced', EmailBouncedBanner], ['phone', PhoneLinkBanner], ['demo', DemoModeBanner],
]

export default function DashboardLayout() {
  return (
    <PageTitleProvider>
      <DashboardLayoutInner />
    </PageTitleProvider>
  )
}

// Per-viewer convenience only: a blocked or empty store just shows the menu.
const SIDEBAR_HIDDEN_KEY = 'lipaflow.sidebarHidden'

function readSidebarHidden(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_HIDDEN_KEY) === '1'
  } catch {
    return false
  }
}

function DashboardLayoutInner() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  const drawer = useDrawerFocus<HTMLDivElement>(sidebarOpen, closeSidebar)
  // Desktop only: the operator can put the menu away for more working room.
  const [sidebarHidden, setSidebarHidden] = useState(readSidebarHidden)
  const toggleSidebarHidden = () => {
    setSidebarHidden((prev) => {
      try {
        window.localStorage.setItem(SIDEBAR_HIDDEN_KEY, prev ? '0' : '1')
      } catch {
        // Not remembered; still toggles for this visit.
      }
      return !prev
    })
  }
  const [searchOpen, setSearchOpen] = useState(false)
  // Sections start open; the operator's own collapses are what we remember.
  // (A hard-coded "expanded" list silently hid any section it didn't name.)
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([])
  const { user, tenant, logout, isImpersonating, stopImpersonation } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { navigation } = useNavigation()

  const handleLogout = async () => {
    if (isImpersonating) {
      stopImpersonation()
      navigate('/login')
      return
    }

    try {
      await authService.logout()
    } catch {
      // Continue client logout even when backend token revocation fails.
    }
    await firebaseLogout()
    logout()
    navigate('/login')
  }

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((g) => g !== groupName)
        : [...prev, groupName]
    )
  }

  const logoUrl = tenant?.logo_url

  const renderNavItem = (item: NavItem, isMobile = false) => {
    const hasChildren = 'children' in item && item.children
    const isExpanded = !collapsedGroups.includes(item.name)

    if (hasChildren) {
      return (
        <div key={item.name} className="space-y-0.5">
          <button
            onClick={() => toggleGroup(item.name)}
            aria-expanded={isExpanded}
            className="w-full flex items-center justify-between px-3 py-2 text-ink-faint hover:text-ink-muted transition-colors duration-fast ease-standard"
          >
            <span className="font-outfit text-overline uppercase">
              {item.name}
            </span>
            <ChevronDownIcon
              className={clsx(
                'h-4 w-4 transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
            />
          </button>
          {isExpanded && (
            <div className="space-y-0.5">
              {item.children!.map((child) => (
                <SidebarLink
                  key={child.name}
                  to={child.href}
                  end={child.href === '/'}
                  activePaths={child.tabs?.map((tab) => tab.href)}
                  icon={child.icon}
                  label={child.name}
                  onNavigate={isMobile ? () => setSidebarOpen(false) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      )
    }

    return (
      <SidebarLink
        key={item.name}
        to={item.href!}
        end={item.href === '/'}
        activePaths={item.tabs?.map((tab) => tab.href)}
        icon={item.icon}
        label={item.name}
        onNavigate={isMobile ? () => setSidebarOpen(false) : undefined}
      />
    )
  }

  const userInitial = user?.full_name?.charAt(0) || 'U'
  const roleLabel = user?.role_labels?.join(', ') || 'No active role'

  return (
    <div className="min-h-screen bg-canvas">
      <a href="#main-content" className="skip-link">Skip to content</a>
      <CommandPalette open={searchOpen} setOpen={setSearchOpen} />
      <DemoActionModal />
      {/* Mobile sidebar overlay */}
      <div
        className={clsx(
          'fixed inset-0 z-50 lg:hidden transition-opacity duration-300',
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        <div
          className="fixed inset-0 bg-scrim/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <div
          ref={drawer}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className={clsx(
            'fixed inset-y-0 left-0 w-72 flex flex-col bg-surface border-r border-hairline shadow-overlay',
            'transition-transform duration-normal ease-standard',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <SidebarBrand
            logoUrl={logoUrl}
            tenantName={tenant?.name}
            actionSlot={
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-ink-faint hover:text-ink rounded-button hover:bg-surface-alt transition-colors duration-fast"
                aria-label="Close navigation"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            }
          />
          <nav className="flex-1 min-h-0 px-3 py-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => renderNavItem(item, true))}
          </nav>
          <SidebarUserSection
            userInitial={userInitial}
            userName={user?.full_name}
            userRole={roleLabel}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Desktop sidebar */}
      {/* min-h-0 bounds the column to the window so the nav scrolls; without
          it the column grew to fit every link and the lower ones were
          unreachable below the fold. */}
      <aside
        id="desktop-sidebar"
        className={clsx('hidden lg:fixed lg:inset-y-0 lg:w-64 lg:flex-col', sidebarHidden ? 'lg:hidden' : 'lg:flex')}
      >
        <div className="flex flex-col flex-grow min-h-0 bg-surface border-r border-hairline">
          <SidebarBrand
            logoUrl={logoUrl}
            tenantName={tenant?.name}
          />
          <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => renderNavItem(item, false))}
          </nav>
          <SidebarUserSection
            userInitial={userInitial}
            userName={user?.full_name}
            userRole={roleLabel}
            tenantName={tenant?.name}
            onLogout={handleLogout}
          />
        </div>
      </aside>

      {/* Main content */}
      <div className={clsx(!sidebarHidden && 'lg:pl-64')}>
        {/* Inside the content column, beside the fixed menu: at full width
            they ran underneath it and lost their first words. */}
        {/* One banner at a time (2026-10-16), the most important first: each
            decides whether it applies, and any after the first that does
            stays hidden until that one is dealt with. */}
        <div data-testid="banners" className="[&>*:not(:empty)~*:not(:empty)]:hidden">
          {BANNERS.map(([id, Banner]) => <div key={id} data-banner={id}><Banner /></div>)}
        </div>
        <TopBar
          onOpenSidebar={() => setSidebarOpen(true)}
          sidebarHidden={sidebarHidden}
          onToggleSidebar={toggleSidebarHidden}
          onOpenSearch={() => setSearchOpen(true)}
          userInitial={userInitial}
          fallbackPath={location.pathname}
          navigation={navigation}
        />

        {/* Page content */}
        <main id="main-content" tabIndex={-1} className="px-4 sm:px-6 py-8 pb-24 lg:pb-10 max-w-container mx-auto space-y-8">
          <SectionTabs navigation={navigation} />
          <Outlet />
        </main>

        <footer className="px-4 sm:px-6 py-6 border-t border-hairline">
          <div className="max-w-container mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="font-inter text-caption text-ink-faint">
              Powered by LipaFlow
            </p>
            <p className="font-inter text-caption text-ink-faint">
              Billing, payments and records. Connected.
            </p>
          </div>
        </footer>
      </div>

      <MobileBottomNav onOpenMenu={() => setSidebarOpen(true)} navigation={navigation} />
    </div>
  )
}

// ── Sidebar sub-components ────────────────────────────────────────────

interface SidebarLinkProps {
  to: string
  end?: boolean
  icon?: React.ElementType
  label: string
  onNavigate?: () => void
  /** For an entry whose pages show as tabs: lit on any of them. */
  activePaths?: string[]
}

function SidebarLink({ to, end, icon: Icon, label, onNavigate, activePaths }: SidebarLinkProps) {
  const { pathname } = useLocation()
  const onTab = activePaths?.some((path) => pathname === path || pathname.startsWith(`${path}/`)) ?? false
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive: linkActive }) => {
        const isActive = linkActive || onTab
        return clsx(
          'group relative flex items-center gap-3 min-h-11 px-3 py-2.5 rounded-button font-inter text-body-sm',
          'transition-colors duration-fast ease-standard',
          isActive
            ? 'bg-accent-soft text-accent font-medium'
            : 'text-ink-muted hover:bg-surface-alt hover:text-ink'
        )
      }}
    >
      {({ isActive: linkActive }) => {
        const isActive = linkActive || onTab
        return (
        <>
          <span
            className={clsx(
              'absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full transition-all duration-fast',
              isActive ? 'bg-accent' : 'bg-transparent group-hover:bg-hairline'
            )}
            aria-hidden
          />
          {Icon && <Icon className="h-5 w-5 shrink-0" />}
          <span className="truncate">{label}</span>
        </>
        )
      }}
    </NavLink>
  )
}

interface SidebarBrandProps {
  logoUrl?: string | null
  tenantName?: string | null
  actionSlot?: React.ReactNode
}

function SidebarBrand({ logoUrl, tenantName, actionSlot }: SidebarBrandProps) {
  return (
    <div className="flex h-16 shrink-0 items-center justify-between gap-2 px-4 border-b border-hairline">
      <Link to="/" className="flex items-center gap-2 min-w-0">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={tenantName || 'Home'}
            className="h-8 max-w-[180px] object-contain"
          />
        ) : (
          // No uploaded logo: the LipaFlow mark with the organisation's name,
          // not a coloured initial and its internal short name ("S seed-rental").
          <span className="min-w-0 truncate">
            <Logo size="sm" markStyle="standalone" subtitle={tenantName || undefined} />
          </span>
        )}
      </Link>
      {actionSlot}
    </div>
  )
}

interface SidebarUserSectionProps {
  userInitial: string
  userName?: string | null
  userRole?: string | null
  tenantName?: string | null
  onLogout: () => void
}

function SidebarUserSection({
  userInitial,
  userName,
  userRole,
  tenantName,
  onLogout,
}: SidebarUserSectionProps) {
  return (
    <div className="p-4 border-t border-hairline">
      {tenantName && (
        <div className="text-caption font-medium text-ink-faint mb-2 truncate">
          {tenantName}
        </div>
      )}
      <div className="flex items-center gap-3">
        {/* Every staff member's own sign-in page, whatever their role. */}
        <Link to="/account" title="Your sign-in" className="flex flex-1 min-w-0 items-center gap-3 rounded-button hover:bg-surface-alt">
          <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
            <span className="font-outfit font-semibold text-body-sm text-on-accent">
              {userInitial}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-inter text-body-sm font-medium text-ink truncate">
              {userName || 'Signed in'}
            </p>
            <p className="font-inter text-caption text-ink-muted truncate" title={userRole || undefined}>
              {userRole || 'Your sign-in'}
            </p>
          </div>
        </Link>
        <button
          onClick={onLogout}
          className="p-2 text-ink-faint hover:text-danger rounded-button hover:bg-danger-soft transition-colors duration-fast"
          title="Sign out"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

// ── Top bar ───────────────────────────────────────────────────────────

interface TopBarProps {
  onOpenSidebar: () => void
  sidebarHidden: boolean
  onToggleSidebar: () => void
  onOpenSearch: () => void
  userInitial: string
  fallbackPath: string
  navigation: NavItem[]
}

function TopBar({ onOpenSidebar, sidebarHidden, onToggleSidebar, onOpenSearch, userInitial, fallbackPath, navigation }: TopBarProps) {
  const { title } = usePageTitleContext()
  const verticalModule = useVerticalModule()

  const derivedTitle = useMemo(() => {
    if (title) return title
    if (fallbackPath === '/') return 'Home'
    // Pages shown as tabs under one entry are named by their tab, not the
    // entry (and not "Dashboard", which is what they fell back to).
    const flat = navigation
      .flatMap<{ name: string; href?: string; tabs?: { name: string; href: string }[] }>((item) =>
        'children' in item && item.children ? item.children : [item]
      )
      .flatMap<{ name: string; href?: string }>((entry) => entry.tabs ?? [entry])
    const match = flat.find(
      (it) => it.href && (it.href === fallbackPath || fallbackPath.startsWith(it.href + '/'))
    )
    if (match) return match.name
    // A page this persona reaches from elsewhere (a landlord's Leases, from
    // a unit) is not in their menu; it is still named by its module.
    const own = verticalModule?.navItems?.find((it) => fallbackPath === it.to || fallbackPath.startsWith(it.to + '/'))
    return own?.label ?? ''
  }, [title, fallbackPath, navigation, verticalModule])

  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-hairline bg-surface/80 backdrop-blur px-4 sm:px-6">
      <button
        className="lg:hidden p-2 -ml-2 text-ink-muted hover:text-ink rounded-button hover:bg-surface-alt transition-colors duration-fast"
        onClick={onOpenSidebar}
        aria-label="Open navigation"
      >
        <Bars3Icon className="h-6 w-6" />
      </button>
      <button
        type="button"
        className="hidden lg:inline-flex p-2 -ml-2 text-ink-muted hover:text-ink rounded-button hover:bg-surface-alt transition-colors duration-fast"
        onClick={onToggleSidebar}
        aria-controls="desktop-sidebar"
        aria-expanded={!sidebarHidden}
        aria-label={sidebarHidden ? 'Show menu' : 'Hide menu'}
        title={sidebarHidden ? 'Show menu' : 'Hide menu'}
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      <div className="min-w-0 flex-1">
        <h2 className="font-outfit text-h4 text-ink truncate">
          {derivedTitle}
        </h2>
      </div>

      <button
        type="button"
        onClick={onOpenSearch}
        className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-button bg-surface-alt text-ink-muted hover:text-ink transition-colors duration-fast"
        title="Command palette"
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
        <span className="font-inter text-body-sm">Search</span>
        <span className="ml-3 hidden lg:inline font-mono text-caption text-ink-faint">
          {isMac ? '⌘K' : 'Ctrl K'}
        </span>
      </button>

      <div className="flex items-center gap-1">
        <DarkModeToggle />
        <NotificationBell />
      </div>

      <Link
        to="/account"
        title="Your sign-in"
        aria-label="Your sign-in"
        className="lg:hidden w-9 h-9 rounded-full bg-accent flex items-center justify-center"
      >
        <span className="font-outfit font-semibold text-body-sm text-on-accent">
          {userInitial}
        </span>
      </Link>
    </header>
  )
}

// ── Mobile Bottom Tab Bar ────────────────────────────────────────────
function MobileBottomNav({
  onOpenMenu,
  navigation,
}: {
  onOpenMenu: () => void
  navigation: NavItem[]
}) {
  const location = useLocation()
  const pageFor = useSharedPage()
  const payments = pageFor('/payments-received')

  // Only offer a tab the operator can actually open, so a teacher without
  // finance access never taps into a redirect. "Menu" opens the full
  // sidebar -- it used to link to /settings, which most staff cannot see.
  const allowed = new Set(
    navigation.flatMap((item) =>
      item.children ? item.children.map((c) => c.href) : item.href ? [item.href] : [],
    ),
  )
  // The menu's own word for each page, so the phone bar and the menu agree
  // (rental's payments page is "Rent received").
  const nameFor = (path: string, fallback: string): string => {
    for (const item of navigation) {
      for (const entry of item.children ?? [item]) {
        if (entry.href && entry.href.split('?')[0] === path) return entry.name
      }
    }
    return fallback
  }
  // A vertical with its own messages page (rental's Messages) is linked there.
  const messages = allowed.has('/messages') ? '/messages' : '/conversations'
  const candidates = [
    { label: 'Home', path: '/', matchPaths: ['/'], icon: HomeIcon },
    // A vertical with its own payments page (rental's Rent received) is linked there.
    ...(payments ? [{ label: nameFor(payments.split('?')[0], 'Payments'), path: payments.split('?')[0], matchPaths: [payments.split('?')[0], '/payments-received', '/approvals', '/deposits-to-assign', '/sponsorships', '/payment-plans', '/pledges'], icon: BanknotesIcon }] : []),
    { label: nameFor(messages, 'Messages'), path: messages, matchPaths: [messages, '/conversations', '/escalations', '/broadcasts', '/templates'], icon: ChatBubbleLeftRightIcon },
    { label: 'Reports', path: '/reports', matchPaths: ['/reports', '/insights'], icon: ChartBarSquareIcon },
  ]
  const tabs = candidates.filter((tab) => tab.path === '/' || allowed.has(tab.path))

  const tabClass = (isActive: boolean) =>
    clsx(
      'flex flex-col items-center justify-center flex-1 h-full text-xs',
      isActive ? 'text-accent' : 'text-ink-faint',
    )

  return (
    <nav className="safe-bottom fixed bottom-0 left-0 right-0 z-40 border-t border-hairline bg-surface lg:hidden" aria-label="Quick navigation">
      <div className="flex items-center justify-around h-16">
        {tabs.map((tab) => {
          const isActive = tab.matchPaths.some((p) =>
            p === '/' ? location.pathname === '/' : location.pathname.startsWith(p),
          )
          return (
            <Link key={tab.path} to={tab.path} aria-current={isActive ? 'page' : undefined} className={tabClass(isActive)}>
              <tab.icon className="h-5 w-5 mb-1" />
              {tab.label}
            </Link>
          )
        })}
        <button type="button" onClick={onOpenMenu} className={tabClass(false)}>
          <EllipsisHorizontalCircleIcon className="h-5 w-5 mb-1" />
          Menu
        </button>
      </div>
    </nav>
  )
}

// ── Section tabs ──────────────────────────────────────────────────────

/**
 * The tabs of a menu entry that groups several pages (Payments, Arrears,
 * Messages, Reports, Settings), above whichever of them is open. Each tab is
 * a page of its own, so links, permissions and the back button still work.
 */
function SectionTabs({ navigation }: { navigation: NavItem[] }) {
  const { pathname } = useLocation()
  const entry = hubForPath(navigation, pathname)
  if (!entry?.tabs) return null
  return (
    <nav aria-label={entry.name} className="-mb-2 flex gap-1 overflow-x-auto border-b border-hairline">
      {entry.tabs.map((tab) => (
        <NavLink
          key={tab.href}
          to={tab.href}
          // A tab whose address starts another tab's (/settings and /settings/team) is active only on its own.
          end={entry.tabs!.some((other) => other.href !== tab.href && other.href.startsWith(`${tab.href}/`))}
          className={({ isActive }) =>
            clsx(
              'whitespace-nowrap border-b-2 px-3 py-2.5 font-inter text-body-sm transition-colors duration-fast',
              isActive ? 'border-accent text-accent font-medium' : 'border-transparent text-ink-muted hover:text-ink',
            )
          }
        >
          {tab.name}
        </NavLink>
      ))}
    </nav>
  )
}
